import { useState, useMemo, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { Phone, Mail, Check } from 'lucide-react'
import { contactCTAs } from './config'
import type { ChatMessageProps, ContactCTA } from '@/types/chatbot'

function parseMarkdownBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2)
      return <strong key={index} className="font-semibold">{boldText}</strong>
    }
    return part
  })
}

const ctaIcons: Record<string, typeof Phone> = {
  phone: Phone,
  email: Mail,
}

function CopyButton({ cta }: { cta: ContactCTA }) {
  const { t } = useTranslation('common')
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(cta.value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: do nothing
    }
  }, [cta.value])

  const Icon = copied ? Check : ctaIcons[cta.id] || Phone

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500 hover:text-primary-600 transition-colors"
    >
      <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
      <span>{copied ? t('chatbot.cta.copied') : t(cta.labelKey)}</span>
    </button>
  )
}

export function ChatMessage({ message, isTyping = false }: ChatMessageProps) {
  const isUser = message.role === 'user'

  const formattedContent = useMemo(() => {
    if (!message.content) return null
    return parseMarkdownBold(message.content.trim())
  }, [message.content])

  return (
    <div className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          isUser
            ? 'bg-primary-500 text-secondary-900 rounded-br-sm'
            : 'bg-white text-secondary-900 rounded-bl-sm shadow-sm border border-[#e3e3e3]'
        )}
      >
        {isTyping ? (
          <div className="flex gap-1.5 py-1 px-1">
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '150ms' }}
            />
            <span
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '300ms' }}
            />
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap leading-relaxed m-0">
            {formattedContent}
          </p>
        )}
      </div>

      {/* Contact CTAs - copy to clipboard */}
      {!isUser && !isTyping && message.content && contactCTAs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 ml-1">
          {contactCTAs.map((cta) => (
            <CopyButton key={cta.id} cta={cta} />
          ))}
        </div>
      )}
    </div>
  )
}
